import React from 'react';
import { Button, IconButton, Input, InputGroup, Stack } from 'rsuite';
import PlusIcon from '@rsuite/icons/Plus';
import TrashIcon from '@rsuite/icons/Trash';
import ArrowUpIcon from '@rsuite/icons/ArrowUp';
import ArrowDownIcon from '@rsuite/icons/ArrowDown';

export const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const DEFAULT_NEW_COLOR = '#0B4FA8';

interface ColorListInputProps {
  /** Ordered colors. Undefined and [] both mean "none configured". */
  value?: string[];
  onChange?: (value: string[]) => void;
  /** Mirrors the API's [HexColorList] cap. */
  maxCount?: number;
  disabled?: boolean;
}

/**
 * Ordered hex-color list editor. Order is meaningful — index 0/1 drive the
 * captain profile picture gradient in the app — hence the reorder controls
 * rather than a plain set of swatches.
 *
 * The native colour picker cannot express an alpha channel, so the hex field
 * stays editable: an admin can type "#0B4FA8CC" and the picker just shows the
 * RGB part.
 */
const ColorListInput = React.forwardRef<HTMLDivElement, ColorListInputProps>(
  ({ value, onChange, maxCount = 8, disabled }, ref) => {
    const colors = value ?? [];

    const emit = (next: string[]) => onChange?.(next);

    const update = (index: number, color: string) =>
      emit(colors.map((c, i) => (i === index ? color : c)));

    const remove = (index: number) => emit(colors.filter((_, i) => i !== index));

    const move = (index: number, delta: number) => {
      const target = index + delta;
      if (target < 0 || target >= colors.length) return;
      const next = [...colors];
      [next[index], next[target]] = [next[target], next[index]];
      emit(next);
    };

    const add = () => emit([...colors, DEFAULT_NEW_COLOR]);

    // Only well-formed colors go into the preview — a half-typed hex would
    // otherwise make the strip flicker to transparent while the admin types.
    const previewColors = colors.filter(c => HEX_COLOR_REGEX.test(c));

    return (
      <div ref={ref}>
        <Stack direction="column" alignItems="flex-start" spacing={8}>
          {colors.map((color, index) => (
            <Stack key={index} spacing={6} alignItems="center">
              <input
                type="color"
                disabled={disabled}
                // A malformed value would make the native picker fall back to
                // black and silently rewrite what the admin typed.
                value={HEX_COLOR_REGEX.test(color) ? color.slice(0, 7) : DEFAULT_NEW_COLOR}
                onChange={e => update(index, e.target.value.toUpperCase())}
                style={{
                  width: 38,
                  height: 34,
                  padding: 0,
                  border: '1px solid #3c3f43',
                  borderRadius: 4,
                  background: 'none',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              />
              <InputGroup style={{ width: 150 }}>
                <Input
                  value={color}
                  disabled={disabled}
                  placeholder="#RRGGBB"
                  onChange={next => update(index, String(next).toUpperCase())}
                />
              </InputGroup>
              <IconButton
                size="sm"
                icon={<ArrowUpIcon />}
                disabled={disabled || index === 0}
                onClick={() => move(index, -1)}
                title="Move earlier"
              />
              <IconButton
                size="sm"
                icon={<ArrowDownIcon />}
                disabled={disabled || index === colors.length - 1}
                onClick={() => move(index, 1)}
                title="Move later"
              />
              <IconButton
                size="sm"
                color="red"
                appearance="subtle"
                icon={<TrashIcon />}
                disabled={disabled}
                onClick={() => remove(index)}
                title="Remove"
              />
            </Stack>
          ))}

          <Button
            size="sm"
            appearance="ghost"
            startIcon={<PlusIcon />}
            disabled={disabled || colors.length >= maxCount}
            onClick={add}
          >
            Add color
          </Button>

          {previewColors.length > 0 && (
            <div style={{ width: '100%' }}>
              <div
                style={{
                  height: 28,
                  width: '100%',
                  maxWidth: 320,
                  borderRadius: 4,
                  border: '1px solid #3c3f43',
                  background:
                    previewColors.length === 1
                      ? previewColors[0]
                      : `linear-gradient(135deg, ${previewColors.join(', ')})`,
                }}
              />
            </div>
          )}
        </Stack>
      </div>
    );
  }
);

export default ColorListInput;
